import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { lastValueFrom, Observable, of } from 'rxjs'
import { CookieService } from 'ngx-cookie-service'
import {
  IConfig,
  ILoginResponseModel,
  InstalledModule,
  IVersioning,
} from '../models/login-response.model'
import { IUserModel } from '../models/user.model'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { HttpResponseHandlerService } from './http-response-handler.service'
import { UserGroupsEnum } from '../enums/user-groups.enum'
import { InstalledModulesEnum } from '../enums/installed-modules.enum'
import { LocalStorageService } from './local-storage.service'
import { FilterStorageService } from './filter-storage.service'
import { environment } from '../../environments/environment'

@Injectable({
  providedIn: 'root',
})
export class BasicAuthService {
  private apiURL: string = environment.apiUrl + '/auth'
  private backendURL: string = environment.backendUrl
  private _useAD: boolean = environment.useAD
  private _useAzureAD: boolean = environment.useAzureAD
  private _isRedirected: boolean = false
  private appCookieName: string = '__spas_app'
  private userCookieName: string = '__spas_usr'
  private sessionCookieName: string = '__spas_session'
  private _jwtToken: string | null = null
  private user: IUserModel = null
  private modules: InstalledModule[] = null
  private versioning: IVersioning = { release: null, backend: null, frontend: null, database: null }
  private config: IConfig = { wla: null, pto_tit: {def: null, max: null} }
  private timezoneOffset: number = null

  public get isRedirected(): boolean {
    return this._isRedirected
  }

  constructor(
    public router: Router,
    private http: HttpClient,
    private modalService: NgbModal,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private filterStorageService: FilterStorageService,
    private handlerResponseService: HttpResponseHandlerService,
  ) {}

  public get useAD(): boolean {
    return this._useAD
  }

  public get useAzureAD(): boolean {
    return this._useAzureAD
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json')
  }

  public async checkResetPasswordToken(token: string): Promise<boolean> {
    try {
      const observable: Observable<any> = this.http.post<any>(
        this.apiURL + '/check-reset-password-token?t=' + encodeURIComponent(token),
        {},
        {
          headers: this.getHeaders(),
          withCredentials: true,
        },
      )
      await lastValueFrom(observable)
      return true
    } catch (e) {
      this.handlerResponseService.notifyError(e)
      return false
    }
  }

  public async forgotPassword(email: string): Promise<void> {
    try {
      const observable: Observable<any> = this.http.post<any>(
        this.apiURL + '/forgot-password',
        { email: email },
        { headers: this.getHeaders(), withCredentials: true },
      )
      await lastValueFrom(observable)
      this.handlerResponseService.notifySuccess('Success!', 'Your request was sent with success!')
      await this.router.navigate(['auth/login'])
    } catch (e) {
      this.handlerResponseService.notifyError(e)
    }
  }

  public async resetPassword(token: string, data: any): Promise<void> {
    try {
      const observable: Observable<any> = this.http.post<any>(
        this.apiURL + '/reset-password?t=' + encodeURIComponent(token),
        data,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        },
      )
      await lastValueFrom(observable)

      this.handlerResponseService.notifySuccess('Success!', 'Your request was sent with success!')
      await this.router.navigate(['auth/login'])
    } catch (e) {
      this.handlerResponseService.notifyError(e)
    }
  }

  public getInstalledModules(): Observable<InstalledModule[]> {
    return this.http.get<any>(this.apiURL + '/installed-modules', {
      headers: this.getHeaders(),
      withCredentials: true,
    })
  }

  private async _loginHttpCall(data: any, headers: HttpHeaders): Promise<ILoginResponseModel> {
    const observable: Observable<ILoginResponseModel> = this.http.post<ILoginResponseModel>(
      this.apiURL + '/ldap/login',
      data,
      {
        headers: headers,
        withCredentials: true,
      },
    )
    return lastValueFrom(observable)
  }

  private async _logoutHttpCall(headers: HttpHeaders): Promise<any> {
    const observable: Observable<any> = this.http.post<any>(
      this.apiURL + '/logout',
      {},
      {
        headers: headers,
        withCredentials: true,
      },
    )
    return lastValueFrom(observable)
  }

  public async login(username: string, password: string, remember?: any): Promise<void> {
    const data: any = {
      username: username,
      password: password,
      remember: remember ? 1 : 0,
    }
    try {
      const result: ILoginResponseModel = await this._loginHttpCall(data, this.getHeaders())
      this.saveData(result.jwt_token)
      this.handlerResponseService.notifySuccess(
        'Logged In',
        'You have successfully logged in to SPAS!',
      )
      const timestamp = new Date().getTime();
      if (result.redirect) {
        this._isRedirected = true
        window.location.href = `${window.location.origin}/?fr=${timestamp}#/user/change-password`
      } else {
        window.location.href = `${window.location.origin}/?fr=${timestamp}#/spas/dashboard`
      }
    } catch (e) {
      this.handlerResponseService.notifyError(e)
    }
  }

  public async simpleLogin(username: string, password: string, remember?: any): Promise<void> {
    const data: any = {
      username: username,
      password: password,
      remember: remember ? 1 : 0,
    }
    try {
      const observable: Observable<ILoginResponseModel> = this.http.post<ILoginResponseModel>(
        this.apiURL + '/login',
        data,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        },
      )
      const result: ILoginResponseModel = await lastValueFrom(observable)
      this.saveData(result.jwt_token)
      this.handlerResponseService.notifySuccess(
        'Logged In',
        'You have successfully logged in to SPAS!',
      )
      const timestamp = new Date().getTime();
      window.location.href = `${window.location.origin}/?fr=${timestamp}#/spas/dashboard`
    } catch (e) {
      this.handlerResponseService.notifyError(e)
    }
  }

  public async connectWithSSO(): Promise<void> {
    try {
      const observable: Observable<ILoginResponseModel> = this.http.get<ILoginResponseModel>(
        this.backendURL + '/sso/connect',
        {
          withCredentials: true,
        },
      )
      const result: ILoginResponseModel = await lastValueFrom(observable)
      this.saveData(result.jwt_token)
      this.handlerResponseService.notifySuccess(
        'Logged In',
        'You have successfully logged in to SPAS!',
      )
      const timestamp = new Date().getTime();
      window.location.href = `${window.location.origin}/?fr=${timestamp}#/spas/dashboard`
    } catch (e) {
      this.handlerResponseService.notifyError(e)
    }
  }

  public async connectWithAzure(uuid: string): Promise<void> {
    const data: any = {
      uuid: uuid,
    }
    try {
      const observable: Observable<ILoginResponseModel> = this.http.post<ILoginResponseModel>(
        this.apiURL + '/azure/connect',
        data,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        },
      )
      const result: ILoginResponseModel = await lastValueFrom(observable)
      this.saveData(result.jwt_token)
      this.handlerResponseService.notifySuccess(
        'Logged In',
        'You have successfully logged in to SPAS!',
      )
      const timestamp = new Date().getTime();
      window.location.href = `${window.location.origin}/?fr=${timestamp}#/spas/dashboard/dashboard-tiles`
    } catch (e) {
      this.handlerResponseService.notifyError(e)
      await this.router.navigate(['auth/login'])
    }
  }

  private saveData(jwtToken?: string): void {
    if (jwtToken) {
      this._jwtToken = jwtToken
    }

    this.filterStorageService.allowFilterSaving = true
  }

  private async clearData(): Promise<void> {
    this._jwtToken = null
    this.user = null
    this.modules = []
    this.versioning = null
    this.config = null
    this.filterStorageService.clearFilters()
    this.filterStorageService.allowFilterSaving = false
    this.localStorageService.clear()
  }

  public isAuthenticated(): Observable<boolean> {
    // __spas_session is a non-HttpOnly flag cookie set by the server alongside the HttpOnly JWT cookie.
    // It lets the frontend detect an active session without ever touching the actual token.
    return of(this.cookieService.check(this.sessionCookieName))
  }

  private clearSession(): void {
    this.cookieService.delete(this.sessionCookieName)
  }

  public getToken(): string | null {
    return this._jwtToken
  }

  public setToken(token: string): void {
    this._jwtToken = token
  }

  public getModules(): InstalledModule[] {
    if (this.modules === null) {
      let jsonData: any
      if ((jsonData = this.cookieService.get(this.appCookieName))) {
        this.extractAppDataFromCookie(jsonData)
      } else {
        this.modules = []
      }
    }
    return this.modules
  }

  public getVersioning(): IVersioning {
    if (this.versioning === null) {
      let jsonData: string
      if ((jsonData = this.cookieService.get(this.appCookieName))) {
        this.extractAppDataFromCookie(jsonData)
      }
    }
    return this.versioning
  }

  public getConfig(): IConfig {
    if (this.config === null) {
      let jsonData: string
      if ((jsonData = this.cookieService.get(this.appCookieName))) {
        this.extractAppDataFromCookie(jsonData)
      }
    }
    return this.config
  }

  public getTimezoneOffset(): number|null {
    if (this.timezoneOffset === null) {
      let jsonData: string
      if ((jsonData = this.cookieService.get(this.appCookieName))) {
        this.extractAppDataFromCookie(jsonData)
      }
    }
    return this.timezoneOffset
  }

  public refreshAppModules(modules: InstalledModule[]): void {
    this.modules = modules
  }

  public refreshConfig(config: IConfig): void {
    this.config = config
  }

  public getUserRole(): string | null {
    this.getUser()
    if (this.user) {
      return this.user.group
    }
    return null
  }

  public getUserName(): string {
    this.getUser()
    if (this.user) {
      return this.user.UserName + ' ' + this.user.UserFirstName
    }
    return ''
  }

  public canSaveFilters(): boolean {
    return (
      [
        UserGroupsEnum.SPAS_Admin.toString(),
        UserGroupsEnum.SPAS_CBMLead.toString(),
        UserGroupsEnum.SPAS_RCALead.toString(),
        UserGroupsEnum.SPAS_Operator.toString(),
      ].indexOf(this.getUserRole()) !== -1
    )
  }

  public canProofApprove(): boolean {
    return (
      [
        UserGroupsEnum.SPAS_Admin.toString(),
        UserGroupsEnum.SPAS_CBMLead.toString(),
        UserGroupsEnum.SPAS_RCALead.toString(),
      ].indexOf(this.getUserRole()) !== -1
    )
  }

  public canSaveDataCard(): boolean {
    return (
      [
        UserGroupsEnum.SPAS_Admin.toString(),
        UserGroupsEnum.SPAS_CBMLead.toString(),
        UserGroupsEnum.SPAS_RCALead.toString(),
        UserGroupsEnum.SPAS_Operator.toString(),
      ].indexOf(this.getUserRole()) !== -1
    )
  }

  public isAdmin(): boolean {
    return [
      UserGroupsEnum.SPAS_Admin.toString(),
    ].indexOf(this.getUserRole(),
    ) !== -1
  }

  public isCBMLead(): boolean {
    return [
      UserGroupsEnum.SPAS_CBMLead.toString(),
    ].indexOf(this.getUserRole(),
    ) !== -1
  }

  public hasExtendedAdminAccess(): boolean {
    return (
      [
        UserGroupsEnum.SPAS_Admin.toString(),
        UserGroupsEnum.SPAS_CBMLead.toString(),
        UserGroupsEnum.SPAS_RCALead.toString(),
      ].indexOf(this.getUserRole()) !== -1
    )
  }

  public hasConfiguratorAccess(): boolean {
    return (
      [
        UserGroupsEnum.SPAS_Configurator.toString()
      ].indexOf(
        this.getUserRole(),
      ) !== -1
    )
  }

  public hasCMMSPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.CMMS) !== -1
    )
  }

  public hasRCAFPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.RCAF) !== -1
    )
  }

  public hasADIntegration(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.AD) !== -1
    )
  }

  public hasDICIntegration(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.DIC) !== -1
    )
  }

  public hasSHDLightPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.SHD_LIGHT) !== -1
    )
  }

  public hasSHDPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.SHD) !== -1
    )
  }

  public hasBLEDefaultPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.BLE_DEFAULT) !== -1
    )
  }

  public hasMultiSiteFilterPackage(): boolean {
    return (
      this.getModules()
        .map(m => m['name'].toUpperCase())
        .indexOf(InstalledModulesEnum.MULTI_SITE_FILTER) !== -1
    )
  }

  private extractAppDataFromCookie(jsonData: string): void {
    const data: any = JSON.parse(jsonData)
    this.modules = data && data.m ? data.m : []
    this.versioning = data && data.v ? data.v : null
    this.config = data && data.c ? data.c : null
    this.timezoneOffset = data && data.tzo ? data.tzo : null
  }

  private extractUserDataFromCookie(jsonData: string): void {
    const data: any = JSON.parse(jsonData)
    this.user = data && data.u ? data.u : null
  }

  public async logout(skipServer: boolean = false): Promise<void> {
    if (skipServer === false) {
      try {
        await this._logoutHttpCall(this.getHeaders())
      } catch (e) {}
    }
    this.modalService.dismissAll()
    this.clearSession()
    await this.clearData()
    await this.toLogin()
  }

  public async toLogin(): Promise<void> {
    await this.router.navigate(['auth/login'])
  }

  public getUser(): IUserModel {
    if (this.user === null) {
      let jsonData: any
      if ((jsonData = this.cookieService.get(this.userCookieName))) {
        this.extractUserDataFromCookie(jsonData)
      }
    }
    return this.user
  }
}
